/**
 * Checkpoint — the Brief 013 respawn-point override (closes the 007 B4 deferral).
 *
 * A NON-solid, NON-hazard overlap entity (collision model = Coin/Item: the player
 * passes through it). On FIRST touch it calls ctx.activateCheckpoint(cx, feetY) so
 * future deaths respawn HERE instead of PLAYER_START, then flips to its "activated"
 * sprite. Idempotent within a life (once `activated`, re-touch is a no-op). Plays the
 * select SFX only when the respawn point actually changed — so respawning ONTO the
 * active checkpoint (which re-overlaps it) re-lights the sprite silently rather than
 * re-jingling. Hearts-reset-on-death semantics are unchanged (C3): the override moves
 * only the respawn POSITION; the world still rebuilds to initial state on death, which
 * is exactly why this has zero replay cost (no mid-level snapshot to restore).
 *
 * Determinism: a deterministic triangle-wave bob (Coin pattern) — Math.abs/floor only,
 * NO Math.sin. ESLint-clean under src/engine/.
 */
import type { Entity, EntityContext } from '../Entity';
import { EntityType } from '../LevelFormat';
import type { AABB } from '../TileGrid';

/** Pixels the banner bobs up/down from its rest Y (subtle idle life). */
const BOB_AMPLITUDE = 3;
/** Full bob cycle (s). */
const BOB_PERIOD = 1.6;

export class Checkpoint implements Entity {
  readonly type = EntityType.CHECKPOINT;
  body: AABB;
  vx = 0;
  vy = 0;
  facing: 1 | -1 = 1;
  alive = true;

  /** Spawn point this checkpoint claims as the respawn override (px convention). */
  private readonly cx: number;
  private readonly feetY: number;
  private readonly restY: number;
  private bobPhase = 0;
  /** True once touched THIS life → idempotent re-touch + activated sprite. */
  private activated = false;

  /**
   * @param cx     spawn center-x (world px)
   * @param feetY  bottom of the entity's tile cell (world px)
   * @param _props unused; accepted for factory-signature consistency
   */
  constructor(cx: number, feetY: number, _props?: Record<string, unknown>) {
    this.cx = cx;
    this.feetY = feetY;
    // A ~48×56 banner sitting in the cell, base at the cell bottom.
    const w = 48, h = 56;
    this.restY = feetY - h;
    this.body = { x: cx - w / 2, y: this.restY, w, h };
  }

  update(dt: number, _ctx: EntityContext): void {
    this.bobPhase = (this.bobPhase + dt) % BOB_PERIOD;
    const t = this.bobPhase / BOB_PERIOD;
    const triangle = 1 - Math.abs(2 * t - 1); // [0..1]
    this.body.y = this.restY + BOB_AMPLITUDE * (triangle * 2 - 1); // ±amplitude
  }

  onPlayerOverlap(ctx: EntityContext): void {
    if (this.activated) return; // idempotent within this life
    const changed = ctx.activateCheckpoint(this.cx, this.feetY);
    this.activated = true; // light the sprite regardless (re-light is silent below)
    if (changed) ctx.playSound('checkpoint', 0.5); // POLISH_01 #1 — dedicated checkpoint chime (was 'select')
  }

  spriteKey(): string {
    return this.activated ? 'checkpoint_active' : 'checkpoint';
  }

  visible(): boolean { return true; }
}
